import { HTMLAttributes, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import { IFormImperativeHandleProps } from '../../../../../common/form';
import { VpUpdateModal } from '../../../../../common/vp-update-modal';
import { GadgetsUseDurationTitle, GadgetsUseDurationView } from '../../vision-profile-views/gadgets-use-duration-view';
import { IGadgetsUseDurationModalProps } from './gadgets-use-duration-modal.models';
import { promiseErrorCallbacks } from '../../../../../../utils/promise/set-promise-error-callbacks';
import { TGadgetsUseDurationValues } from '../../vision-profile-views/gadgets-use-duration-view/gadgets-use-duration-form';
import { GET_VISION_PROFILE } from '../../../../../../graphql/profile/queries/get-vision-profile';
import { UPDATE_VISION_PROFILE } from '../../../../../../graphql/profile/mutations';
import { IVisionProfileData } from '../../../../../../graphql/profile/models/get-vision-profile.models';
import { useCancellablePromise } from '../../../../../../hooks/use-cancellable-promise';
import { useActionsInProgress } from '../../../../../../graphql/preloader/actions/actions-in-progress';

export const GadgetsUseDurationModal = ({ gadgetsUseDuration, onClose, ...props }: IGadgetsUseDurationModalProps): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { updateQuery } = useQuery<IVisionProfileData>(GET_VISION_PROFILE);
  const [updateVisionProfile] = useMutation(UPDATE_VISION_PROFILE);
  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const defaultValues = useMemo(() => ({ gadgetsUseDuration }), []);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleGadgetsUseDurationChange = async ({
    gadgetsUseDuration: gadgetsUseDurationCurrent,
  }: TGadgetsUseDurationValues) => {
    addActionInProgress();

    try {
      const { data: { patchVisionProfile } } = await makeCancellablePromise(
        updateVisionProfile({
          variables: {
            visionProfile: {
              gadgetsUseDuration: gadgetsUseDurationCurrent,
            },
          },
        }),
      );

      updateQuery((prevState) => ({
        visionProfile: {
          ...prevState.visionProfile,
          ...patchVisionProfile,
        },
      }));

      onClose!();
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }
    }

    removeActionInProgress();
  };

  return (
    <VpUpdateModal
      {...props}
      PaperProps={{
        'data-testid': 'vp-gadgets-use-duration-modal',
      } as HTMLAttributes<HTMLDivElement>}
      title={<GadgetsUseDurationTitle />}
      content={(
        <GadgetsUseDurationView
          ref={formRef}
          formProps={{
            onSubmit: handleGadgetsUseDurationChange,
            defaultValues,
          }}
        />
      )}
      onVPUpdate={handleSubmit}
      onClose={onClose}
    />
  );
};
